import type { ComponentType } from 'react';

import { Box } from '@bonusly/design-system';

import type { ApprovalDecision } from '../../../lib/bizy/types/chat';
import type { BizyAssistantMessageNode } from '../../../lib/bizy/types/nodes';
import {
	BizyChatActionsProvider,
	BizyUiBlockActionProvider,
	type BizyChatActions,
	type BizyGiveBoxDraft,
	type BizyUiBlockActionDispatcher,
	type BizyWrapInfo,
} from './context/actions-context';
import { BizyAnalyticsProvider, type BizyTrackFn } from './context/analytics-context';
import { BizyDoodleUploadProvider, type BizyDoodleUpload } from './context/doodle-upload-context';
import { BizyGifProvider, type BizyGifSearch } from './context/gif-context';
import { BizyLinkProvider, type BizyLinkHandler } from './context/link-context';
import { BizyUserResolverProvider, type BizyUserResolver } from './context/user-resolver-context';
import { ErrorBoundary } from './error-boundary';
import { defaultRegistry, type BizyNodeComponent } from './registry';
import { UnsupportedNode } from './unsupported-node';

// Legacy card nodes share a cumulative `cardIndex` so per-card choice tracking
// (`nodeChoices[cardIndex]`) targets the right entry across the document.
const LEGACY_CARD_TYPES = new Set(['approval_card', 'content_card', 'draft_card', 'meeting_people_card']);

export interface DocumentRendererProps {
	/** Ordered sequence of typed nodes — markdown interleaved with cards. */
	nodes: BizyAssistantMessageNode[];
	/** Override the node-type → component map (defaults to {@link defaultRegistry}). */
	registry?: Map<string, BizyNodeComponent>;
	/** Override the fallback renderer for unknown / malformed node types. */
	fallback?: ComponentType<{ node?: BizyAssistantMessageNode }>;

	// Document config / state.
	fontSize?: 'sm' | 'md';
	isComplete?: boolean;
	messageKey?: string;
	conversationId?: string;
	toolProgress?: string[];
	thinkingPhrase?: string;
	nodeChoices?: Record<number, ApprovalDecision>;
	isReadOnly?: boolean;
	followUpsDisabled?: boolean;

	// Injected behavior (host-supplied; sensible no-op defaults otherwise).
	onLinkClick?: BizyLinkHandler;
	track?: BizyTrackFn;
	searchGifs?: BizyGifSearch;
	resolveUser?: BizyUserResolver;
	dispatchUiBlockAction?: BizyUiBlockActionDispatcher;
	/** Uploads a rasterized doodle PNG and returns its hosted URL. */
	uploadDoodle?: BizyDoodleUpload;

	// Document callbacks.
	onContentUpdate?: () => void;
	onConfirm?: (cardIndex: number, confirmMessage: string) => void;
	onChoiceUpdate?: (messageKey: string, cardIndex: number, choice: ApprovalDecision) => void;
	onSelectFollowUp?: (suggestion: string) => void;
	onDrill?: (message: string) => void;
	onSuggestedPageClick?: (path: string) => void;
	onOpenGiveBox?: (draft: BizyGiveBoxDraft) => void;
	/** Post-wrap toggle data for the recognition draft card (host-resolved). */
	wrap?: BizyWrapInfo;
}

/**
 * Top-level renderer for a typed Bizy assistant message. Walks the node array
 * in order, dispatches each node to its registered component (falling back to
 * {@link UnsupportedNode} for unknown types), and wraps each node in an error
 * boundary so one malformed node fails gracefully instead of taking down the
 * whole message.
 *
 * Cross-cutting behavior (links, analytics, gif search, user resolution, card
 * actions, document config/callbacks) is supplied through context providers so
 * the registry can dispatch generically.
 */
export function DocumentRenderer({
	nodes,
	registry = defaultRegistry,
	fallback: Fallback = UnsupportedNode,
	fontSize,
	isComplete = false,
	messageKey,
	conversationId,
	toolProgress,
	thinkingPhrase,
	nodeChoices,
	isReadOnly = false,
	followUpsDisabled = false,
	onLinkClick,
	track,
	searchGifs,
	resolveUser,
	dispatchUiBlockAction,
	uploadDoodle,
	onContentUpdate,
	onConfirm,
	onChoiceUpdate,
	onSelectFollowUp,
	onDrill,
	onSuggestedPageClick,
	onOpenGiveBox,
	wrap,
}: DocumentRendererProps) {
	// Index of the last markdown node — only this one receives streaming UI.
	const lastMarkdownIdx = (() => {
		for (let i = nodes.length - 1; i >= 0; i -= 1) {
			if (nodes[i].type === 'markdown') return i;
		}
		return -1;
	})();

	const actions: BizyChatActions = {
		fontSize,
		isComplete,
		toolProgress,
		thinkingPhrase,
		onContentUpdate,
		isReadOnly,
		followUpsDisabled,
		messageKey,
		nodeChoices,
		onConfirm,
		onChoiceUpdate,
		onSelectFollowUp,
		onDrill,
		onSuggestedPageClick,
		onOpenGiveBox,
		wrap,
	};

	let cardCount = 0;

	return (
		<BizyLinkProvider onLinkClick={onLinkClick}>
			<BizyAnalyticsProvider conversationId={conversationId} messageId={messageKey} track={track}>
				<BizyUserResolverProvider resolveUser={resolveUser}>
					<BizyGifProvider searchGifs={searchGifs}>
						<BizyDoodleUploadProvider uploadDoodle={uploadDoodle}>
							<BizyUiBlockActionProvider dispatch={dispatchUiBlockAction}>
								<BizyChatActionsProvider value={actions}>
									<Box data-testid="bizy-document-renderer">
										{nodes.map((node, idx) => {
											const cardIndex = cardCount;
											if (LEGACY_CARD_TYPES.has(node.type)) cardCount += 1;

											const Component = (registry.get(node.type) ?? Fallback) as BizyNodeComponent;

											return (
												<ErrorBoundary key={idx} fallback={() => <Fallback node={node} />}>
													<Component
														node={node}
														nodeIndex={idx}
														cardIndex={cardIndex}
														isActiveMarkdown={idx === lastMarkdownIdx}
														isLastNode={idx === nodes.length - 1}
													/>
												</ErrorBoundary>
											);
										})}
									</Box>
								</BizyChatActionsProvider>
							</BizyUiBlockActionProvider>
						</BizyDoodleUploadProvider>
					</BizyGifProvider>
				</BizyUserResolverProvider>
			</BizyAnalyticsProvider>
		</BizyLinkProvider>
	);
}
